<script lang="ts">
    import type { PageData } from './$types';
    import { onMount, onDestroy } from 'svelte';
    import { format, parseISO, startOfMonth, eachDayOfInterval, startOfYear, endOfYear } from 'date-fns';
    import { ja } from 'date-fns/locale';
    import { supabase } from '$lib/supabase';
    import { UserPlus, UserMinus, Youtube, Video, Clock, Archive } from 'lucide-svelte';
    import LoginPopup from '$lib/components/LoginPopup.svelte';
    import Chart from '$lib/components/Chart.svelte';
    import { DateTimeUtils } from '$lib/utils/dateTimeUtils';
    import { formatDuration } from '$lib/utils/formatUtils';
    import ContentCard from '$lib/components/ContentCard.svelte';
    import { generateTags } from '$lib/utils/tagGenerator';
    import { authStore } from '$lib/stores/authStore';
    import { getMaxConcurrentViewersFromVideos, getAvgConcurrentViewers, getAvgStreamDuration, getAvgStreamsPerWeek } from '$lib/utils/statsUtils';
    import ShareButton from '$lib/components/ShareButton.svelte';
    import { _, locale } from 'svelte-i18n';
    import { hasTranslation } from '$lib/i18n';
    import AmazonAffiliateModule from '$lib/components/AmazonAffiliateModule.svelte';
    import MusicAffiliateModule from '$lib/components/MusicAffiliateModule.svelte';
    
    // 翻訳関数のラッパー
    function t(key: string, defaultValue: string, values?: Record<string, any>): string {
        // 現在の言語を取得
        let currentLocale = 'ja';
        locale.subscribe(value => {
            currentLocale = value || 'ja';
        })();
        
        // 翻訳キーが存在するか確認
        if (hasTranslation(key, currentLocale)) {
            return values ? $_(key, { values }) : $_(key);
        } else {
            return defaultValue;
        }
    }

    export let data: PageData;

    $: ({ 
        channel,
        liveStream,
        upcomingStreams,
        archivedVideos,
        channelName,
        groupName,
        viewData,
        channelStats,
        amazonProducts
    } = data);

    let isFollowing = false;

    onMount(async () => {
        const user = $authStore.user;

        if (user) {
            const { data } = await supabase
                .from('follows')
                .select('id')
                .eq('user_id', user.id)
                .eq('channel_id', channel.id)
                .single();
            
            isFollowing = !!data;
        }
    });

    let showLoginPopup = false;

    async function toggleFollow() {
        if (!$authStore.user) {
            showLoginPopup = true;
            return;
        }

        if (isFollowing) {
            const { error } = await supabase
                .from('follows')
                .delete()
                .eq('user_id', $authStore.user.id)
                .eq('channel_id', channel.id);

            if (error) console.error('Error unfollowing:', error);
            else isFollowing = false;
        } else {
            const { error } = await supabase
                .from('follows')
                .insert({ 
                    user_id: $authStore.user.id, 
                    channel_id: channel.id 
                });

            if (error) console.error('Error following:', error);
            else isFollowing = true;
        }
    }

    function getYoutubeChannelUrl(channelId: string): string {
        return `https://www.youtube.com/channel/${channelId}`;
    }

    let elapsedTime = '';
    let interval;

    function updateElapsedTime() {
        if (liveStream && liveStream.scheduled_start_time) {
            const startTime = DateTimeUtils.convertToISODate(liveStream.scheduled_start_time);
            const now = new Date();
            const seconds = Math.floor((now.getTime() - startTime.getTime()) / 1000);
            elapsedTime = DateTimeUtils.formatTimeToHHMMSS(seconds);
        }
    }

    onMount(async () => {
        updateElapsedTime();
        interval = setInterval(updateElapsedTime, 1000);
    });

    onDestroy(() => {
        if (interval) {
            clearInterval(interval);
        }
    });

    let archivedVideosByMonth = {};
    let monthlyTags = {};
    let selectedMonth = '';
    let displayedVideos = [];
    let availableMonths = [];

    function handleMonthSelect(month) {
        selectedMonth = month;
        updateDisplayedVideos();
    }

    // Chartコンポーネトからの月選択イベントを処理する関数
    function onMonthChange(event) {
        handleMonthSelect(event.detail.month);
    }

    onMount(async () => {
        processArchivedVideos();
        if (availableMonths.length > 0) {
            handleMonthSelect(availableMonths[0]);
        }
        // @ts-ignore
        (window.adsbygoogle = window.adsbygoogle || []).push({});

        // Amazon商品データがない場合は、デフォルトのデータを使用
        if (!amazonProducts || amazonProducts.length === 0) {
            // チャンネルに関連するAmazon商品データ
            amazonItems = [
                {
                    id: "B08GLVZ7YM",
                    title: "SMC組 公式グッズ Tシャツ",
                    imageUrl: "https://m.media-amazon.com/images/I/71R2ZTZ7zGL._AC_SL1500_.jpg",
                    price: "¥3,980"
                },
                {
                    id: "B09B1P59QR",
                    title: "にじさんじ メモリアルグッズセット 2021",
                    imageUrl: "https://m.media-amazon.com/images/I/61xPH9VgL5L._AC_SL1500_.jpg",
                    price: "¥5,500"
                },
                {
                    id: "B097TVLST5",
                    title: "VTuber マルチタッチペン",
                    imageUrl: "https://m.media-amazon.com/images/I/61xGy7cGSkL._AC_SL1200_.jpg",
                    price: "¥1,480"
                }
            ];
        } else {
            // サーバーから取得した商品データを使用
            amazonItems = amazonProducts;
        }
    });

    let allTags = [];

    function processArchivedVideos(groupName: string = '') {
        archivedVideosByMonth = archivedVideos.reduce((acc, video) => {
            const monthKey = format(parseISO(video.published_at), 'yyyy-MM');
            if (!acc[monthKey]) {
                acc[monthKey] = [];
            }
            acc[monthKey].push(video);
            return acc;
        }, {});

        availableMonths = Object.keys(archivedVideosByMonth).sort((a, b) => b.localeCompare(a));

        // 全体のタグを生成
        const allTitles = archivedVideos.map(video => video.title).join(' ');
        allTags = generateTags(allTitles, channelName);
    }

    let selectedTag: string | null = null;

    function handleMonthChange(event) {
        selectedMonth = event.detail.month;
        selectedTag = null; // 月が変更されたらフィルターをリセット
        updateDisplayedVideos();
    }

    function handleTagClick(tag) {
        if (selectedTag === tag) {
            selectedTag = null;
        } else {
            selectedTag = tag;
        }
        updateDisplayedVideos();
    }

    function updateDisplayedVideos() {
        archivedVideosByMonth = {};

        availableMonths.forEach(month => {
            let videos = archivedVideos.filter(video => {
                const videoMonth = format(parseISO(video.published_at), 'yyyy-MM');
                return videoMonth === month;
            });

            if (selectedTag && selectedTag !== 'すべて') {
                videos = videos.filter(video => generateTags(video.title, channelName).includes(selectedTag));
            }

            archivedVideosByMonth[month] = videos;
        });
    }

    $: {
        if (selectedMonth) {
            updateDisplayedVideos();
        }
    }

    function getTagCount(tag: string): number {
        if (tag === t('common.all', 'すべて')) {
            return archivedVideos.length;
        } else {
            return archivedVideos.filter(video => generateTags(video.title, channelName).includes(tag)).length;
        }
    }

    function groupChannels() {
        groupedChannels = data.groups.reduce((acc, group) => {
            acc[group.id] = {
                name: group.name,
                channels: data.channels.filter(channel => channel.group === group.id),
            };
            return acc;
        }, {});
    }

    function generateSEOData(channel) {
    const totalVideos = archivedVideos?.length || 0;
    const latestStream = archivedVideos?.[0];
    const popularTags = generateTags(archivedVideos?.map(v => v.title).join(' '), channel.name)
        .slice(0, 4)
        .join('・');

    // タイトルの生成
    const title = groupName 
        ? `${channel.name}（${groupName}）- | VTGrid`
        : `${channel.name} - VTuber配信 | VTGrid`;
    // 説明文の生成（優先順位順）
    let description = `${channel.name}`;
    
    // 人気コンテンツ（あれば）
    if (popularTags) {
        description += `の${popularTags}など、`;
    }

    // アーカイブ数
    if (totalVideos > 0) {
        description += `${totalVideos}本`;
    }

    // グループ名（あれば）
    if (groupName) {
        description += `${groupName}所属。`;
    }

    // 配信タグ（あれば）
    if (channel.live_hashtag) {
        description += `配信タグ：${channel.live_hashtag}`;
    }

    // キーワードの生成
    const keywords = [
        channel.name,
        groupName,
        'VTuber',
        'バーチャルYouTuber',
        '動画',
        'ライブ配信',
        channel.live_hashtag?.replace('#', ''),
        channel.fanart_hashtag?.replace('#', ''),
        ...popularTags.split('・')
    ].filter(Boolean).join(', ');

    // SNSリンクの生成
    const socialLinks = [
        `https://www.youtube.com/channel/${channel.id}`,
        channel.twitch_id && `https://www.twitch.tv/${channel.twitch_id}`,
        channel.twitter_id && `https://twitter.com/${channel.twitter_id}`
    ].filter(Boolean);

    return {
        title,
        description: description.slice(0, 160), // 160文字で制限
        keywords,
        totalVideos,
        popularTags,
        socialLinks,
        structuredData: {
            "@context": "https://schema.org",
            "@type": "ProfilePage",
            "mainEntity": {
                "@type": "Person",
                "name": channel.name,
                "image": channel.thumbnail,
                "url": `https://vt-grid.com/channel/${channel.id}`,
                "sameAs": socialLinks,
                "description": description,
                "interactionStatistic": {
                    "@type": "InteractionCounter",
                    "interactionType": "https://schema.org/WatchAction",
                    "userInteractionCount": totalVideos
                }
            }
        }
    };
}

    $: seoData = channel ? generateSEOData(channel) : null;

    let amazonItems: Array<{
        id: string;
        title: string;
        imageUrl: string;
        price?: string;
        url?: string;
    }> = [];
</script>

<svelte:head>
    <title>{channel.name} - VTuber配信統計・ライブ情報 | VTGrid</title>
    <meta name="description" content={`${channel.name}の配信統計データ、ライブ配信情報、アーカイブ動画をまとめて確認できます。平均視聴者数${channelStats.avgConcurrentViewers.toLocaleString()}人、週間配信${channelStats.avgStreamsPerWeek.toFixed(1)}回。${groupName ? `${groupName}所属。` : ''}過去の配信アーカイブや視聴者数推移、人気コンテンツも分析。`} />
    <meta name="keywords" content={`${channel.name},VTuber,配信統計,ライブ配信,${groupName || ''},視聴者数,配信時間,アーカイブ,${allTags.slice(0, 5).join(',')}`} />
    
    <meta property="og:title" content={`${channel.name} - VTuber配信統計・ライブ情報 | VTGrid`} />
    <meta property="og:type" content="website" />
    <meta property="og:url" content={`https://vt-grid.com/channel/${channel.id}`} />
    <meta property="og:image" content={channel.thumbnail || ''} />
    <meta property="og:site_name" content="VTGrid" />
    <meta property="og:description" content={`${channel.name}の配信統計データ、ライブ配信情報、アーカイブ動画をまとめて確認できます。平均視聴者数${channelStats.avgConcurrentViewers.toLocaleString()}人、週間配信${channelStats.avgStreamsPerWeek.toFixed(1)}回。${groupName ? `${groupName}所属。` : ''}過去の配信アーカイブや視聴者数推移、人気コンテンツも分析。`} />
    
    <meta name="twitter:card" content="summary" />
    <meta name="twitter:site" content="@vtgrid" />
    <meta name="twitter:title" content={`${channel.name} - VTuber配信統計・ライブ情報 | VTGrid`} />
    <meta name="twitter:description" content={`${channel.name}の配信統計データ、ライブ配信情報、アーカイブ動画をまとめて確認できます。平均視聴者数${channelStats.avgConcurrentViewers.toLocaleString()}人、週間配信${channelStats.avgStreamsPerWeek.toFixed(1)}回。${groupName ? `${groupName}所属。` : ''}過去の配信アーカイブや視聴者数推移、人気コンテンツも分析。`} />
    <meta name="twitter:image" content={channel.thumbnail || ''} />
    
    <link rel="canonical" href={`https://vt-grid.com/channel/${channel.id}`} />
</svelte:head>
{#if channel && ContentCard}
    <div class="text-gray-900 dark:text-white min-h-screen">
        <div class="container mx-auto px-1 py-1">
            <div class="p-3">
                <div class="flex flex-col sm:flex-row justify-between items-start gap-4 mb-8">
                    <div class="flex items-center gap-4">
                        <img 
                            src={channel.thumbnail} 
                            alt={channel.name} 
                            class="w-20 h-20 rounded-full"
                        />
                        <div>
                            <h1 class="text-xl font-bold mb-2">{channel.name}</h1>
                            {#if groupName}
                                <a 
                                    href="/group/{channel.group_id}" 
                                    class="inline-flex items-center gap-2 px-2 py-1 text-sm text-black/80 dark:text-white/80 hover:text-black dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/5 rounded-md transition-colors"
                                    aria-label="{groupName}のグループページへ"
                                >
                                    <span class="relative flex-shrink-0 w-4 h-4">
                                        {#if channel.groups?.icon_url}
                                            <img 
                                                src={channel.groups.icon_url} 
                                                alt="" 
                                                class="w-full h-full rounded-full bg-black/5 dark:bg-white/5 ring-1 ring-black/20 dark:ring-white/20"
                                                loading="lazy"
                                                aria-hidden="true"
                                            />
                                        {:else}
                                            <svg 
                                                xmlns="http://www.w3.org/2000/svg" 
                                                class="w-full h-full text-black/70 dark:text-white/70" 
                                                viewBox="0 0 20 20" 
                                                fill="currentColor"
                                                aria-hidden="true"
                                            >
                                                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                                            </svg>
                                        {/if}
                                    </span>
                                    <span class="font-medium">{groupName}</span>
                                </a>
                            {/if}
                        </div>
                    </div>
                    
                    <div class="flex flex-col gap-4">
                        <div class="flex flex-wrap gap-6">
                            <div class="flex flex-col">
                                <span class="text-2xl font-medium tracking-tight">{channelStats.subscriberCount.toLocaleString()}</span>
                                <span class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">登録者数</span>
                            </div>
                            <div class="flex flex-col">
                                <span class="text-2xl font-medium tracking-tight">{channelStats.maxConcurrentViewers.toLocaleString()}</span>
                                <span class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">最高同時接続数</span>
                            </div>
                            <div class="flex flex-col">
                                <span class="text-2xl font-medium tracking-tight">{channelStats.avgConcurrentViewers.toLocaleString()}</span>
                                <span class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">平均同時接続数</span>
                            </div>
                            <div class="flex flex-col">
                                <span class="text-2xl font-medium tracking-tight">{formatDuration(channelStats.avgStreamDuration)}</span>
                                <span class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">平均配信時間</span>
                            </div>
                            <div class="flex flex-col">
                                <span class="text-2xl font-medium tracking-tight">{channelStats.avgStreamsPerWeek.toLocaleString()}回</span>
                                <span class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">週平均配信回数</span>
                            </div>
                        </div>

                        <div class="flex gap-2">
                            <button 
                                class="flex-1 py-2 px-4 rounded-xl text-sm font-medium
                                    {isFollowing 
                                        ? 'bg-zinc-100 dark:bg-zinc-800 text-black/60 dark:text-white/60' 
                                        : 'bg-zinc-900 dark:bg-white text-white dark:text-black'} 
                                    transition-all duration-200"
                                on:click={toggleFollow}
                            >
                                {#if isFollowing}
                                    <UserMinus class="w-5 h-5 mr-2 inline-block" />
                                    {t('channel.following', 'フォロー中')}
                                {:else}
                                    <UserPlus class="w-5 h-5 mr-2 inline-block" />
                                    {t('channel.follow', 'フォローする')}
                                {/if}
                            </button>
                            <ShareButton
                                url={`/channel/${channel.id}`}
                                title={channel.name}
                                class="rounded-xl bg-zinc-100 dark:bg-zinc-800/50"
                            />
                        </div>
                    </div>
                </div>

                <div class="grid grid-cols-2 sm:grid-cols-2 gap-4">
                    <div class="flex items-center">
                        <Youtube class="w-6 h-6 text-red-500 mr-2" />
                        <a href={getYoutubeChannelUrl(channel.id)} target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-500 transition-colors duration-200">
                            YouTube
                        </a>
                    </div>
                    
                    {#if channel.twitch_id}
                        <div class="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-purple-500 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z"/>
                            </svg>
                            <a href="https://www.twitch.tv/{channel.twitch_id}" target="_blank" rel="noopener noreferrer" class="text-purple-500 hover:text-purple-600 dark:text-purple-400 dark:hover:text-purple-500 transition-colors duration-200">
                                {channel.twitch_id}
                            </a>
                        </div>
                    {/if}
                    
                    {#if channel.twitter_id}
                    <div class="flex items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-500 mr-2" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M23.643 4.937c-.835.37-1.732.62-2.675.733.962-.576 1.7-1.49 2.048-2.578-.9.534-1.897.922-2.958 1.13-.85-.904-2.06-1.47-3.4-1.47-2.572 0-4.658 2.086-4.658 4.66 0 .364.042.718.12 1.06-3.873-.195-7.304-2.05-9.602-4.868-.4.69-.63 1.49-.63 2.342 0 1.616.823 3.043 2.072 3.878-.764-.025-1.482-.234-2.11-.583v.06c0 2.257 1.605 4.14 3.737 4.568-.392.106-.803.162-1.227.162-.3 0-.593-.028-.877-.082.593 1.85 2.313 3.198 4.352 3.234-1.595 1.25-3.604 1.995-5.786 1.995-.376 0-.747-.022-1.112-.065 2.062 1.323 4.51 2.093 7.14 2.093 8.57 0 13.255-7.098 13.255-13.254 0-.2-.005-.402-.014-.602.91-.658 1.7-1.477 2.323-2.41z"/>
                        </svg>
                        <a href="https://twitter.com/{channel.twitter_id}" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-500 transition-colors duration-200">
                            @{channel.twitter_id}
                        </a>
                    </div>
                    {/if}
                    
                    {#if channel.live_hashtag}
                        <div class="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-blue-500 mr-2" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M7.548 21.501c9.056 0 14.01-7.503 14.01-14.01 0-.213 0-.425-.015-.636A10.02 10.02 0 0024 4.305a9.828 9.828 0 01-2.828.776 4.94 4.94 0 002.165-2.724 9.867 9.867 0 01-3.127 1.195 4.929 4.929 0 00-8.391 4.491A13.98 13.98 0 011.67 3.9a4.928 4.928 0 001.525 6.573A4.887 4.887 0 01.96 9.855v.063a4.926 4.926 0 003.95 4.827 4.917 4.917 0 0 0 2.415 0 5.972 5.972 0 0 0 2.148-.903c.313-.212.612-.458.886-.731.272-.271.52-.571.734-.889l-1.658-1.119a4.017 4.017 0 0 1-1.489 1.592z"/>
                            </svg>
                            <a href="https://twitter.com/hashtag/{channel.live_hashtag}" target="_blank" rel="noopener noreferrer" class="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-500 transition-colors duration-200">
                                #{channel.live_hashtag}
                            </a>
                        </div>
                    {/if}

                    {#if channel.fanart_hashtag}
                        <div class="flex items-center">
                            <svg xmlns="http://www.w3.org/2000/svg" class="w-6 h-6 text-pink-500 mr-2" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M12 2C6.486 2 2 6.486 2 12s4.486 10 10 10 10-4.486 10-10S17.514 2 12 2zm0 18c-4.411 0-8-3.589-8-8s3.589-8 8-8 8 3.589 8 8-3.589 8-8 8z"/>
                            <path d="M14.829 14.828a4.055 4.055 0 0 1-1.272.858 4.002 4.002 0 0 1-4.875-1.45l-1.658 1.119a6.063 6.063 0 0 0 1.621 1.62 5.963 5.963 0 0 0 2.148.903 6.042 6.042 0 0 0 2.415 0 5.972 5.972 0 0 0 2.148-.903c.313-.212.612-.458.886-.731.272-.271.52-.571.734-.889l-1.658-1.119a4.017 4.017 0 0 1-1.489 1.592z"/>
                            <path d="M12 6.5a2.5 2.5 0 1 0 0 5 2.5 2.5 0 0 0 0-5zm0 3.5c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z"/>
                            </svg>
                            <a href="https://twitter.com/hashtag/{channel.fanart_hashtag}" target="_blank" rel="noopener noreferrer" class="text-pink-500 hover:text-pink-600 dark:text-pink-400 dark:hover:text-pink-500 transition-colors duration-200">
                                #{channel.fanart_hashtag}
                            </a>
                        </div>
                    {/if}
                </div>
            </div>

            <!-- ライブ配信セクション -->
            {#if liveStream && liveStream.id}
                <div class="mt-12">
                    <h2 class="text-xl font-bold mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
                        ライブ配信中
                    </h2>
                    <div class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                        <ContentCard 
                            id={liveStream.id}
                            thumbnailUrl={liveStream.thumbnail_url}
                            title={liveStream.title}
                            type="live"
                            elapsedTime={elapsedTime}
                            channelId={channel.id}
                            liveStats={liveStream.live_stats}
                            platform={liveStream.platform}
                            liveChatHistory={liveStream.liveChatHistory}
                            liveBroadcastContent={liveStream.live_broadcast_content}
                        />
                    </div>
                </div>
            {/if}

            <!-- 配信予定セクション -->
            {#if upcomingStreams.length > 0}
                <div class="mt-12 pb-5">
                    <h2 class="text-xl font-bold mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
                        配信予定
                    </h2>
                    <div class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                        {#each upcomingStreams as stream (stream.id)}
                            <ContentCard 
                                id={stream.id}
                                thumbnailUrl={stream.thumbnail_url}
                                title={stream.title}
                                type="upcoming"
                                scheduledStartTime={stream.scheduled_start_time}
                                platform={stream.platform}
                                liveBroadcastContent={stream.live_broadcast_content}
                            />
                        {/each}
                    </div>
                </div>
            {/if}

            <ins class="adsbygoogle"
                style="display:block"
                data-ad-client="ca-pub-1653216533599284"
                data-ad-slot="5897106771"
                data-ad-format="auto"
                data-full-width-responsive="true"></ins>

            <!-- Amazonアフィリエイトモジュール -->
            <!-- <AmazonAffiliateModule
                title="関連商品"
                items={amazonItems}
                affiliateId="vtgrid01-22"
            />

            <!-- <div class="mt-4 p-0 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-zinc-800 dark:to-zinc-900 rounded-lg overflow-hidden shadow-sm border border-black/5 dark:border-white/5">
                <div class="flex flex-col">
                    <div class="p-3 border-b border-black/5 dark:border-white/5 bg-white/50 dark:bg-black/20">
                        <h3 class="font-bold text-black/90 dark:text-white/90">おすすめ商品</h3>
                    </div>
                    <a 
                        href={channel.group_id === 2 ? "https://amzn.to/4jzSN6p" : 
                             channel.group_id === 3 ? "https://amzn.to/44TPWAz" : 
                             "https://amzn.to/3GCOCYV"} 
                        target="_blank" 
                        rel="noopener noreferrer sponsored" 
                        class="flex items-center gap-4 p-4 hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                    >
                        <div class="w-16 h-16 flex-shrink-0 bg-white rounded-lg overflow-hidden flex items-center justify-center shadow-sm border border-black/5 dark:border-white/10">
                            {#if channel.groups?.icon_url}
                                <img 
                                    src={channel.groups.icon_url} 
                                    alt={groupName || ""} 
                                    class="w-full h-full object-cover"
                                    loading="lazy"
                                />
                            {:else}
                                <div class="w-full h-full bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center text-white">
                                    <svg xmlns="http://www.w3.org/2000/svg" class="w-8 h-8" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M15.9,5.5l0-0.1C15.9,5.1,16,4.7,16,4.4C16,4.2,16,3.9,15.9,3.6c-0.1-0.3-0.2-0.5-0.3-0.7 c-0.1-0.2-0.3-0.4-0.4-0.5c-0.2-0.1-0.4-0.3-0.6-0.3C14.3,2,14.1,2,13.9,2c-0.5,0-0.9,0.1-1.4,0.3C11.9,2.5,11.5,2.6,11,2.7 C10.5,2.8,10.1,2.9,9.6,2.9C9.1,2.9,8.7,2.9,8.3,2.9L8.1,2.9c-0.4,0-0.7,0-0.9,0.1C7,3.1,6.9,3.1,6.7,3.2C6.6,3.3,6.5,3.3,6.3,3.4 C6.2,3.5,6.1,3.7,6,3.9C6,4,5.9,4.2,5.9,4.4c0,0.4,0.1,0.7,0.2,0.9c0.1,0.3,0.3,0.5,0.5,0.7c0.2,0.2,0.4,0.3,0.7,0.4 c0.3,0.1,0.5,0.2,0.8,0.3c0.3,0.1,0.6,0.1,0.9,0.2c0.3,0,0.6,0.1,0.9,0.1c0.1,0,0.3,0,0.4,0.1c0.1,0,0.3,0.1,0.4,0.1 c0.1,0.1,0.2,0.1,0.3,0.2c0.1,0.1,0.1,0.2,0.1,0.4c0,0.2-0.1,0.4-0.2,0.5c-0.1,0.1-0.3,0.3-0.4,0.3c-0.2,0.1-0.4,0.2-0.6,0.2 c-0.2,0-0.4,0.1-0.7,0.1c-0.3,0-0.6,0-0.8-0.1c-0.3-0.1-0.5-0.2-0.7-0.3c-0.2-0.1-0.4-0.3-0.5-0.4c-0.1-0.2-0.3-0.3-0.4-0.5 c-0.1-0.1-0.2-0.2-0.4-0.2c-0.2,0-0.3,0.1-0.5,0.3c-0.1,0.2-0.2,0.3-0.2,0.6c0,0.2,0.1,0.4,0.2,0.6c0.1,0.2,0.3,0.4,0.5,0.6 c0.2,0.2,0.5,0.4,0.8,0.5c0.3,0.2,0.7,0.3,1.1,0.4c0.4,0.1,0.8,0.1,1.3,0.1c0.4,0,0.8,0,1.2-0.1c0.4-0.1,0.7-0.2,1-0.4 c0.3-0.2,0.6-0.4,0.8-0.7c0.2-0.3,0.3-0.7,0.3-1.1c0-0.5-0.1-0.8-0.3-1.1c-0.2-0.3-0.5-0.6-0.8-0.8c-0.3-0.2-0.7-0.3-1.1-0.4c-0.4-0.1-0.8-0.2-1.2-0.2c-0.4,0-0.8-0.1-1.2-0.1c-0.4,0-0.7-0.1-1-0.2 c-0.3-0.1-0.5-0.2-0.7-0.3c-0.2-0.1-0.2-0.3-0.2-0.5c0-0.2,0.1-0.3,0.2-0.4c0.1-0.1,0.3-0.2,0.4-0.3c0.2-0.1,0.4-0.1,0.6-0.1 c0.2,0,0.4,0,0.6,0c0.3,0,0.5,0,0.8,0.1c0.2,0.1,0.5,0.1,0.7,0.3c0.2,0.1,0.3,0.2,0.4,0.4c0.1,0.2,0.2,0.3,0.3,0.5L15.9,5.5L15.9,5.5z M21.3,16.1c-1.9,1.4-4.7,2.1-7,2.1c-3.3,0-6.3-1.2-8.6-3.3c-0.2-0.2,0-0.4,0.2-0.3c2.5,1.4,5.5,2.3,8.6,2.3c2.1,0,4.4-0.4,6.6-1.3 C21.4,15.5,21.7,15.8,21.3,16.1z M22.4,14.9c-0.2-0.3-1.6-0.2-2.2-0.1c-0.2,0-0.2-0.1-0.1-0.3c1.1-0.8,2.9-0.5,3.1-0.3 c0.2,0.3-0.1,2.3-1.2,3.2c-0.2,0.1-0.3,0.1-0.3-0.1C22,16.6,22.6,15.2,22.4,14.9z"/>
                                    </svg>
                                </div>
                            {/if}
                        </div>
                        <div class="flex-1 min-w-0">
                            <p class="text-sm font-medium text-black/90 dark:text-white/90 line-clamp-2">
                                {channel.group_id === 1 ? "にじさんじのおすすめ商品をチェック！" : 
                                 channel.group_id === 2 ? "ホロライブのおすすめ商品をチェック！" : 
                                 channel.group_id === 3 ? "ぶいすぽっ！のおすすめ商品をチェック！" : 
                                 "おすすめ商品をチェック！"}
                            </p>
                            <p class="text-xs text-black/60 dark:text-white/60 mt-1.5">
                                Amazon.co.jpで購入する
                            </p>
                        </div>
                        <div class="flex-shrink-0 ml-auto">
                            <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 text-black/30 dark:text-white/30" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                                <path d="M9 18l6-6-6-6"/>
                            </svg>
                        </div>
                    </a>
                </div>
            </div> -->

            <div class="mt-4">
                <h2 class="text-xl font-bold mb-4 pb-2 border-b border-gray-200 dark:border-gray-700">
                    過去の配信
                </h2>
            </div>


            <!-- チャートセクション -->
            <div class="">
                {#if viewData[channel.id]}
                    <Chart
                        data={Object.fromEntries(Object.entries(viewData[channel.id]).map(([month, data]) => [month, data.data]))}
                        labels={Object.fromEntries(Object.entries(viewData[channel.id]).map(([month, data]) => [month, data.labels]))}
                        videos={Object.fromEntries(Object.entries(viewData[channel.id]).map(([month, data]) => [month, data.videos]))}
                        on:monthChange={handleMonthChange}
                    />
                {/if}
            </div>

            <!-- 音楽アフィリエイトモジュール -->
            <!-- <MusicAffiliateModule
                artistName={channel.name}
                groupName={groupName || ""}
                title="おすすめ音楽"
                affiliateId="vtgrid01-22"
                customItems={amazonProducts?.filter(product => 
                    product.title.includes('CD') || 
                    product.title.includes('Album') || 
                    product.title.includes('Music') ||
                    product.title.includes('アルバム') ||
                    product.title.includes('楽曲') ||
                    product.title.includes('ミュージック')
                )}
            /> -->

            <!-- アーカイブ動画セクション -->
            <div class="mt-12">
                {#if allTags.length > 0}
                    <div class="relative mb-4">
                        <label 
                            for="tag-filter" 
                            class="block mb-2 text-sm font-medium text-black/80 dark:text-white/80"
                        >
                            タグでフィルター
                        </label>
                        <div class="relative w-full max-w-[280px]">
                            <select 
                                id="tag-filter"
                                class="w-full appearance-none px-4 py-2.5 pr-10 rounded-lg text-sm 
                                bg-white dark:bg-zinc-900
                                border border-black/10 dark:border-white/10
                                text-black/80 dark:text-white/80
                                hover:border-purple-500/50 dark:hover:border-purple-500/50
                                focus:border-purple-500 dark:focus:border-purple-500
                                focus:ring-2 focus:ring-purple-500/20 dark:focus:ring-purple-500/20
                                focus:outline-none
                                transition-all duration-200"
                                value={selectedTag === null ? "すべて" : selectedTag}
                                on:change={(e) => handleTagClick(e.target.value === "すべて" ? null : e.target.value)}
                                aria-label="動画をタグでフィルター"
                                aria-describedby="tag-filter-description"
                            >
                                <option value="すべて">
                                    すべての動画 ({archivedVideos.length})
                                </option>
                                {#each allTags.filter(tag => getTagCount(tag) >= 2) as tag}
                                    <option value={tag}>
                                        {tag} ({getTagCount(tag)})
                                    </option>
                                {/each}
                            </select>
                            <div class="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none text-black/40 dark:text-white/40">
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    width="16" 
                                    height="16" 
                                    viewBox="0 0 24 24" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    stroke-width="2" 
                                    stroke-linecap="round" 
                                    stroke-linejoin="round"
                                    aria-hidden="true"
                                >
                                    <polyline points="6 9 12 15 18 9"></polyline>
                                </svg>
                            </div>
                        </div>
                        <div 
                            id="tag-filter-description" 
                            class="mt-2 text-xs text-black/50 dark:text-white/50"
                        >
                            {#if selectedTag === null}
                                全{archivedVideos.length}件の動画を表示しています。タグを選択して絞り込むことができます。
                            {:else}
                                タグ「{selectedTag}」の動画{getTagCount(selectedTag)}件を表示しています。
                            {/if}
                        </div>
                    </div>
                {/if}

                {#each availableMonths as month}
                    {#if archivedVideosByMonth[month] && archivedVideosByMonth[month].length > 0}
                        <div class="mb-8">
                            <h3 class="text-lg font-semibold mb-4">{format(parseISO(month), 'yyyy年MM月', { locale: ja })}</h3>
                            
                            <div class="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
                                {#each archivedVideosByMonth[month].filter(video => selectedTag === null || video.title.includes(selectedTag)) as video, i}
                                    <ContentCard 
                                        id={video.id}
                                        thumbnailUrl={video.thumbnail_url}
                                        title={video.title}
                                        type="archived"
                                        duration={video.duration}
                                        channelId={channel.id}
                                        publishedAt={video.published_at}
                                        liveStats={video.live_stats}
                                        platform={video.platform}
                                    />
                                {/each}
                            </div>
                        </div>
                    {/if}
                {/each}
            </div>
        </div>
    </div>
{:else if error}
    <div class="bg-white dark:bg-gray-900 text-gray-900 dark:text-white min-h-screen flex items-center justify-center">
        <p class="text-red-500">{error}</p>
    </div>
{:else}
    <div class="bg-white dark:bg-gray-900 text-gray-900 dark:text-white min-h-screen flex items-center justify-center">
        <p>読み込み中...</p>
    </div>
{/if}
{#if showLoginPopup}
    <LoginPopup on:close={() => showLoginPopup = false} />
{/if}


<style>
    .grid-cols-7 {
        grid-template-columns: repeat(7, minmax(0, 1fr));
    }

    @media (max-width: 640px) {
        button {
            padding: 0.5rem 1rem;
            margin-left: 0.5rem;
        }
    }

    @media (min-width: 641px) {
        button {
            padding: 0.25rem 0.75rem;
            margin-left: 0.5rem;
        }
    }
</style>






















